import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowLeft, Plus, Trash2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

export default function TableauDepenses({ user, onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [expenses, setExpenses] = useState([]);
  const [customExpenses, setCustomExpenses] = useState([]);
  const [newExpense, setNewExpense] = useState({ category: '', amount: 0 });
  const readOnly = user?.role === 'associe' || user?.role === 'investisseur';

  useEffect(() => {
    loadFinancialData();
  }, []);

  const loadFinancialData = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/financial/dashboard`);
      setExpenses(response.data.expenses || getDefaultExpenses());
      setCustomExpenses(response.data.customExpenses || []);
    } catch (error) {
      console.error('Error loading data:', error);
      setExpenses(getDefaultExpenses());
    } finally {
      setLoading(false);
    }
  };

  const getDefaultExpenses = () => [
    { id: 1, category: 'Loyer et charges', amount: 2000, fixed: true },
    { id: 2, category: 'Salaires', amount: 3500, fixed: true },
    { id: 3, category: 'Frais de la mairie', amount: 500, fixed: true },
    { id: 4, category: 'Frais de déplacement', amount: 800, fixed: true },
    { id: 5, category: 'Assurance', amount: 400, fixed: true },
    { id: 6, category: 'Frais entretien panneau', amount: 1200, fixed: true },
    { id: 7, category: 'Marketing', amount: 600, fixed: true }
  ];

  const handleAddCustomExpense = () => {
    if (!newExpense.category || newExpense.amount <= 0) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    const expense = {
      id: Date.now(),
      category: newExpense.category,
      amount: parseFloat(newExpense.amount),
      fixed: false
    };

    setCustomExpenses([...customExpenses, expense]);
    setNewExpense({ category: '', amount: 0 });
    toast.success('Dépense ajoutée');
  };

  const handleRemoveCustomExpense = (id) => {
    setCustomExpenses(customExpenses.filter(e => e.id !== id));
    toast.success('Dépense supprimée');
  };

  const handleSaveData = async () => {
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/financial/dashboard`, {
        expenses,
        customExpenses
      });
      toast.success('Données sauvegardées');
    } catch (error) {
      console.error('Error saving data:', error);
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  const allExpenses = [...expenses, ...customExpenses];
  const totalExpenses = allExpenses.reduce((sum, exp) => sum + exp.amount, 0);

  const expensesChartData = allExpenses.map(exp => ({
    name: exp.category,
    value: exp.amount
  }));

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              onClick={() => {
                const isInvestor = user?.role === 'investisseur' || user?.role === 'associe';
                navigate(-1);
              }}
              variant="outline"
              className="gap-2"
              data-testid="depenses-back-btn"
            >
              <ArrowLeft className="h-4 w-4" />
              Retour
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">📋 Tableau des Dépenses</h1>
              <p className="text-gray-600 mt-1">Gestion des charges mensuelles</p>
            </div>
          </div>
          <Button onClick={handleSaveData} className="gap-2 bg-blue-600 hover:bg-blue-700" style={{ display: readOnly ? 'none' : undefined }}>
            <Save className="h-4 w-4" />
            Sauvegarder
          </Button>
        </div>

        {/* Graphique uniquement */}
        <Card className="shadow-lg">
          <CardHeader className="bg-gradient-to-r from-red-600 to-red-700 text-white">
            <CardTitle>🍰 Répartition des Charges</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <ResponsiveContainer width="100%" height={400}>
              <PieChart>
                <Pie
                  data={expensesChartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={120}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {expensesChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Tableau des Dépenses */}
        <Card className="shadow-lg">
          <CardHeader className="bg-gradient-to-r from-orange-600 to-orange-700 text-white">
            <CardTitle>Détail des Dépenses Mensuelles</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-4">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-orange-200">
                    <th className="px-6 py-4 text-left font-semibold text-gray-700">Date</th>
                    <th className="px-6 py-4 text-left font-semibold text-gray-700">Description</th>
                    <th className="px-6 py-4 text-right font-semibold text-gray-700">Montant (€)</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const currentDate = new Date().toLocaleDateString('fr-FR');
                    
                    return (
                      <>
                        {/* Dépenses fixes */}
                        {expenses.map((exp, index) => (
                          <tr key={exp.id} className={`border-b ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                            <td className="px-6 py-3 font-medium">{currentDate}</td>
                            <td className="px-6 py-3 font-medium">{exp.category}</td>
                            <td className="px-6 py-3 text-right">
                              {readOnly ? (
                                <span className="inline-block w-32 text-right font-semibold text-red-700 pr-3">{(exp.amount || 0).toLocaleString('fr-FR')}</span>
                              ) : (
                                <Input
                                  type="number"
                                  value={exp.amount}
                                  onChange={(e) => {
                                    const updated = expenses.map(item =>
                                      item.id === exp.id ? { ...item, amount: parseFloat(e.target.value) || 0 } : item
                                    );
                                    setExpenses(updated);
                                  }}
                                  className="w-32 text-right"
                                />
                              )}
                            </td>
                          </tr>
                        ))}

                        {/* Dépenses personnalisées */}
                        {customExpenses.map((exp, index) => (
                          <tr key={exp.id} className={`border-b ${(expenses.length + index) % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                            <td className="px-6 py-3 font-medium">{currentDate}</td>
                            <td className="px-6 py-3 font-medium text-purple-700 flex items-center gap-2">
                              {exp.category}
                              {!readOnly && (
                                <Button
                                  onClick={() => handleRemoveCustomExpense(exp.id)}
                                  size="sm"
                                  variant="ghost"
                                  className="h-6 w-6 p-0 text-red-600 hover:bg-red-50"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              )}
                            </td>
                            <td className="px-6 py-3 text-right">
                              {readOnly ? (
                                <span className="inline-block w-32 text-right font-semibold text-red-700 pr-3">{(exp.amount || 0).toLocaleString('fr-FR')}</span>
                              ) : (
                                <Input
                                  type="number"
                                  value={exp.amount}
                                  onChange={(e) => {
                                    const updated = customExpenses.map(item =>
                                      item.id === exp.id ? { ...item, amount: parseFloat(e.target.value) || 0 } : item
                                    );
                                    setCustomExpenses(updated);
                                  }}
                                  className="w-32 text-right"
                                />
                              )}
                            </td>
                          </tr>
                        ))}

                        {/* Formulaire d'ajout (masqué pour les associés) */}
                        {!readOnly && (
                          <tr className="bg-purple-50 border-t-2 border-purple-300">
                            <td className="px-6 py-3 font-medium text-gray-500">{currentDate}</td>
                            <td className="px-6 py-3 flex items-center gap-2">
                              <Input
                                placeholder="Nouvelle description..."
                                value={newExpense.category}
                                onChange={(e) => setNewExpense({ ...newExpense, category: e.target.value })}
                                className="flex-1"
                              />
                              <Button
                                onClick={handleAddCustomExpense}
                                size="sm"
                                className="gap-1 bg-purple-600 hover:bg-purple-700"
                              >
                                <Plus className="h-4 w-4" />
                                Ajouter
                              </Button>
                            </td>
                            <td className="px-6 py-3">
                              <Input
                                type="number"
                                placeholder="Montant"
                                value={newExpense.amount || ''}
                                onChange={(e) => setNewExpense({ ...newExpense, amount: e.target.value })}
                                className="w-32 text-right"
                              />
                            </td>
                          </tr>
                        )}

                        {/* Total */}
                        <tr className="bg-orange-100 border-t-2 border-orange-300">
                          <td className="px-6 py-4 font-bold text-gray-800">{currentDate}</td>
                          <td className="px-6 py-4 font-bold text-gray-800">TOTAL CHARGES</td>
                          <td className="px-6 py-4 text-right font-bold text-orange-700 text-lg">
                            {totalExpenses.toLocaleString()}€
                          </td>
                        </tr>
                      </>
                    );
                  })()}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
